import {
  AlertGroup,
  GenericStatus,
  ManualPreCheckError,
  ValidationItem,
  ValidationStatus,
  ValidationStatusLabels
} from 'vrlcm-gui-types';

import { ControlType } from './PrecheckControl';
import { Label } from '@vmware-react/clarity';
import React from 'react';
import c from 'classnames';
import { formatNumber, l10n } from '../with-app-root/i18n/i18nUtil';

type StatusCount = {
  [status in ValidationStatus]: number;
};

export const executePrerequesite = (actionText: string) => (
  <>
    <span
      dangerouslySetInnerHTML={{
        __html: l10n('components.preCheck.executePrerequisite', [actionText])
      }}
    ></span>
  </>
);
export const ALL_VALIDATIONS_PASSED = () => (
  <>{l10n('components.preCheck.allValidationsPassed')}</>
);
export const CONSENT_REQUIRED = () => (
  <>{l10n('components.preCheck.consentRequired')}</>
);
export const VALIDATIONS_FAILED = () => (
  <>{l10n('components.preCheck.validationsFailed')}</>
);
export const PRECHECK_CANCELLED = () => (
  <>{l10n('components.preCheck.preCheckCancelled')}</>
);
export const RUN_PRECHECK = () =>
  executePrerequesite(l10n('components.preCheck.RUNPRECHECK'));
export const RE_RUN_PRECHECK = () =>
  executePrerequesite(l10n('components.preCheck.RERUNPRECHECK'));
export const GENERAL_ADIVCE = () => (
  <>{l10n('components.preCheck.generalAdvice')}</>
);
const MANUAL_PRECHECK_FAILED = (errorMsg: string) => (
  <>
    {l10n('components.preCheck.manualPreCheckFailed')}
    <span className="info__margin-left">{errorMsg}</span>
  </>
);

export function getStatusIcon(status: ValidationStatus) {
  if (status === 'FIXED') {
    return (
      <clr-icon
        shape="thumbs-up"
        class="is-solid is-info"
        size={18}
        title={l10n('fixed')}
      />
    );
  }

  if (status === 'USER_WARNING') {
    return (
      <clr-icon
        shape="user"
        class="is-solid is-warning"
        size={18}
        title={l10n('warning')}
      />
    );
  }

  if (['FAILED', 'PASSED', 'WARNING'].includes(status)) {
    const iconType =
      status === 'FAILED'
        ? 'error'
        : status === 'PASSED'
        ? 'success'
        : 'warning';

    const title =
      status === 'FAILED'
        ? l10n('error')
        : status === 'PASSED'
        ? l10n('success')
        : l10n('warning');

    return (
      <clr-icon
        shape={`${iconType}-standard`}
        class={`is-solid is-${iconType}`}
        size={18}
        title={title}
      />
    );
  }

  return (
    <clr-icon
      shape="circle"
      class={c({ 'is-solid is-skipped': status === 'SKIPPED' })}
      size={18}
    />
  );
}

export function getStatusCount(items: ValidationItem[]): StatusCount {
  return items.reduce((acc, cur) => {
    const count = acc[cur.status];
    acc[cur.status] = count ? count + 1 : 1;
    return { ...acc };
  }, {} as StatusCount);
}

function getBadgeClass(status: ValidationStatus) {
  switch (status) {
    case 'FAILED':
      return 'danger';
    case 'WARNING':
    case 'USER_WARNING':
      return 'orange';
    case 'PASSED':
      return 'success';
    default:
      return;
  }
}

export function getStatusLabel(items: ValidationItem[]) {
  const statusCount: StatusCount = getStatusCount(items);

  return Object.entries(statusCount).map(([status, count]) => {
    const badgeClass = getBadgeClass(status as ValidationStatus);
    const label = ValidationStatusLabels[status as ValidationStatus];
    return (
      <Label key={status}>
        {label}
        {count && status !== 'PENDING' && status !== 'SKIPPED' && (
          <span className={c('badge', { [`badge-${badgeClass}`]: badgeClass })}>
            {formatNumber(count)}
          </span>
        )}
      </Label>
    );
  });
}

export function getAlertGroups(
  requestStatus: GenericStatus,
  alertConfig?: {
    success?: JSX.Element[];
    failed?: JSX.Element[];
    info?: JSX.Element[];
  },
  manualPrecheckError?: ManualPreCheckError
) {
  const alertGroups: AlertGroup[] = [];

  if (requestStatus === GenericStatus.Successful) {
    alertGroups.push({
      type: 'success',
      items:
        alertConfig && alertConfig.success
          ? alertConfig.success
          : [ALL_VALIDATIONS_PASSED()]
    });
  }

  if (requestStatus === GenericStatus.ConsentRequired) {
    alertGroups.push({
      type: 'warning',
      items: [CONSENT_REQUIRED()]
    });
  }

  if (requestStatus === GenericStatus.Failed) {
    alertGroups.push({
      type: 'danger',
      items:
        alertConfig && alertConfig.failed
          ? alertConfig.failed
          : [VALIDATIONS_FAILED()]
    });
  }

  if (requestStatus === GenericStatus.Cancelled) {
    alertGroups.push({
      type: 'warning',
      items: [PRECHECK_CANCELLED()]
    });
  }

  if (requestStatus === GenericStatus.NotStarted) {
    alertGroups.push({
      type: 'info',
      items:
        alertConfig && alertConfig.info
          ? alertConfig.info
          : [RUN_PRECHECK(), GENERAL_ADIVCE()]
    });
  }

  if (
    requestStatus === GenericStatus.ConsentRequired ||
    requestStatus === GenericStatus.Cancelled ||
    requestStatus === GenericStatus.Failed ||
    requestStatus === GenericStatus.Successful
  ) {
    alertGroups.push({
      type: 'info',
      items:
        alertConfig && alertConfig.info
          ? alertConfig.info
          : [RE_RUN_PRECHECK(), GENERAL_ADIVCE()]
    });
  }

  if (manualPrecheckError?.isError) {
    alertGroups.push({
      type: 'danger',
      items: [MANUAL_PRECHECK_FAILED(manualPrecheckError.errorMessage)]
    });
  }

  return alertGroups.filter(groups => groups.items.length > 0);
}

export function getControlType(requestStatus: GenericStatus): ControlType {
  return requestStatus === GenericStatus.Pending ||
    requestStatus === GenericStatus.Running
    ? 'Loader'
    : 'Action';
}

export function getControlText(requestStatus: GenericStatus): string {
  return requestStatus === GenericStatus.Pending
    ? `${l10n('pleaseWait')}...`
    : requestStatus === GenericStatus.NotStarted
    ? l10n('components.preCheck.runPreCheck')
    : requestStatus === GenericStatus.Failed ||
      requestStatus === GenericStatus.Successful ||
      requestStatus === GenericStatus.Cancelled ||
      requestStatus === GenericStatus.ConsentRequired
    ? l10n('components.preCheck.reRunPreCheck')
    : requestStatus === GenericStatus.Running
    ? `${l10n('components.preCheck.preCheckRunning')}...`
    : l10n('components.preCheck.reRunPreCheck');
}
